import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowDownToLine, Check, FileText } from "lucide-react";
import { toast } from "sonner";
import { apiClient, API } from "../lib/site";
import { getUtm } from "../lib/utm";
import { fadeUp } from "./sections";

export default function LeadMagnetSection() {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/lead-magnet", { ...form, utm: getUtm() });
      toast.success("Thanks. The download is starting now.");
      // trigger download
      const link = document.createElement("a");
      link.href = `${API}/downloads/diligence-checklist`;
      link.download = "Elite-Solutions-Hub-Diligence-Checklist.pdf";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setDone(true);
    } catch (err) {
      toast.error("Couldn't capture that just yet. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="lead-magnet-section"
      className="relative px-6 md:px-12 lg:px-16 py-24 md:py-32 border-y border-white/5 bg-[#050505] overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 grid-bg opacity-40" />
      <div className="absolute -right-32 -top-32 w-[500px] h-[500px] accent-glow -z-10" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        {/* LEFT: PDF Mockup + Pitch */}
        <div className="lg:col-span-7">
          <motion.p {...fadeUp} className="overline mb-6">Field Guide · Issue 01</motion.p>
          <motion.h2
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.05 }}
            className="font-serif-display font-light text-white leading-[1.05] tracking-tight text-balance"
            style={{ fontSize: "clamp(36px, 5.5vw, 72px)", letterSpacing: "-0.025em" }}
          >
            The Software Architecture <em className="italic" style={{ color: "#C2533B" }}>Diligence Checklist.</em>
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="mt-8 text-zinc-400 text-lg leading-relaxed max-w-2xl"
          >
            Forty-three questions every CTO, board, and delivery lead should be able to answer
            in writing — before greenlighting the next platform investment.
          </motion.p>

          <motion.ul
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl"
          >
            {[
              "7 sections, board-ready",
              "Strategic alignment",
              "Architecture & domain",
              "Data & integrity",
              "Security & compliance",
              "Delivery & operations",
              "Team & knowledge",
              "Cost & commercial discipline",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-zinc-300 text-sm">
                <Check size={14} strokeWidth={1.5} style={{ color: "#C2533B" }} className="mt-1 shrink-0" />
                {item}
              </li>
            ))}
          </motion.ul>

          {/* PDF preview card */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-12 hidden md:flex items-center gap-5 border border-[#27272A] bg-[#0A0A0C] p-6 max-w-md"
            data-testid="lead-magnet-pdf-card"
          >
            <div className="w-14 h-16 bg-[#050505] border border-[#27272A] flex items-center justify-center shrink-0">
              <FileText size={20} strokeWidth={1.2} style={{ color: "#C2533B" }} />
            </div>
            <div>
              <p className="text-white text-sm font-medium">Diligence-Checklist.pdf</p>
              <p className="text-zinc-500 text-xs mt-1">A4 · 9 pages · Free · No card required</p>
            </div>
          </motion.div>
        </div>

        {/* RIGHT: Form */}
        <div className="lg:col-span-5 lg:pl-8">
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="border border-[#27272A] bg-[#0A0A0C] p-8 md:p-10"
          >
            {done ? (
              <div data-testid="lead-magnet-success">
                <div className="w-12 h-12 border border-[#C2533B] flex items-center justify-center mb-6">
                  <ArrowDownToLine size={20} strokeWidth={1.5} style={{ color: "#C2533B" }} />
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl text-white font-light">
                  Your download has started.
                </h3>
                <p className="mt-4 text-zinc-400 text-sm leading-relaxed">
                  We've also sent a copy to <span className="text-white">{form.email}</span> so it's
                  saved somewhere easy to find. If it didn't auto-download:
                </p>
                <a
                  href={`${API}/downloads/diligence-checklist`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary mt-6 inline-flex"
                  data-testid="lead-magnet-manual-download"
                >
                  Download Again <ArrowDownToLine size={14} strokeWidth={1.8} />
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="lead-magnet-form">
                <p className="overline">Get the checklist</p>
                <h3 className="font-serif-display text-2xl md:text-3xl text-white font-light leading-snug">
                  Tell us where to send it.
                </h3>
                <input
                  className="input-line"
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="lead-name-input"
                  required
                />
                <input
                  type="email"
                  className="input-line"
                  placeholder="Work email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="lead-email-input"
                  required
                />
                <input
                  className="input-line"
                  placeholder="Company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  data-testid="lead-company-input"
                />
                <input
                  className="input-line"
                  placeholder="Role (e.g., CTO, VP Engineering)"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="lead-role-input"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full disabled:opacity-50"
                  data-testid="lead-submit-button"
                >
                  {submitting ? "Sending…" : "Download the PDF"}{" "}
                  <ArrowDownToLine size={14} strokeWidth={1.8} />
                </button>
                <p className="text-zinc-600 text-xs leading-relaxed">
                  We'll send a copy to your inbox and may follow up once. We don't sell your details
                  and never bombard you. GDPR compliant.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
